import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config';
import './AdminProducts.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    sale_price: '',
    sku: '',
    stock_quantity: '',
    brand: '',
    frame_type: 'full-rim',
    frame_shape: '',
    frame_material: '',
    lens_type: '',
    prescription_type: 'non-prescription',
    gender: 'unisex',
    is_featured: 0,
    is_active: 1
  });

  const [variantData, setVariantData] = useState({
    product_id: '',
    variant_name: 'Color',
    variant_value: '',
    variant_color_code: '',
    price: '',
    sale_price: '',
    stock_quantity: ''
  });
  const [editingVariant, setEditingVariant] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantImages, setVariantImages] = useState([]);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = React.useRef(null);
  const variantFileInputRef = React.useRef(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProducts(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch products');
        }
      } else {
        setError('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCategories(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProductVariants = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/variants`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVariants(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
  };

  const fetchProductImages = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProductImages(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleImageUpload = async (e, type = 'product', variantId = null) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    
    for (const file of files) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/avif'];
      if (!allowedTypes.includes(file.type)) {
        alert(`Invalid file type for ${file.name}`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum 5MB`);
        continue;
      }

      const formDataImg = new FormData();
      formDataImg.append('image', file);
      if (type === 'variant' && variantId) {
        formDataImg.append('variant_id', variantId);
      }
      formDataImg.append('product_id', editingProduct?.id || selectedProduct?.id);

      try {
        const response = await fetch(`${API_BASE_URL}/admin/upload-product-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataImg
        });

        const data = await response.json();
        if (response.ok && data.success) {
          if (type === 'variant' && currentVariant) {
            fetchVariantImages(currentVariant.id);
          } else {
            fetchProductImages(selectedProduct?.id);
          }
          fetchProducts();
        } else {
          alert(data.message || 'Failed to upload image');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Error uploading image');
      }
    }
    
    setUploadingImages(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (variantFileInputRef.current) variantFileInputRef.current.value = '';
  };

  const fetchVariantImages = async (variantId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/variants/${variantId}/images`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setVariantImages(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching variant images:', error);
    }
  };

  const setPrimaryImage = async (imageId, type = 'product') => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/images/${imageId}/set-primary`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ type })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        if (type === 'variant' && currentVariant) {
          fetchVariantImages(currentVariant.id);
        } else {
          fetchProductImages(selectedProduct?.id);
        }
        fetchProducts();
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
    }
  };

  const deleteImage = async (imageId, type = 'product') => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        if (type === 'variant' && currentVariant) {
          fetchVariantImages(currentVariant.id);
        } else {
          fetchProductImages(selectedProduct?.id);
        }
        fetchProducts();
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.price) {
      alert('Product name and price are required');
      return;
    }

    if (!formData.slug) {
      formData.slug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    try {
      const url = editingProduct 
        ? `${API_BASE_URL}/admin/products/${editingProduct.id}` 
        : `${API_BASE_URL}/admin/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert(editingProduct ? 'Product updated successfully' : 'Product created successfully');
        fetchProducts();
        resetForm();
      } else {
        alert(data.message || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product');
    }
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    
    if (!variantData.variant_value) {
      alert('Variant value is required');
      return;
    }

    try {
      const url = editingVariant 
        ? `${API_BASE_URL}/admin/variants/${editingVariant.id}` 
        : `${API_BASE_URL}/admin/variants`;
      const method = editingVariant ? 'PUT' : 'POST';
      
      const payload = { ...variantData, product_id: selectedProduct.id };
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert(editingVariant ? 'Variant updated successfully' : 'Variant created successfully');
        fetchProductVariants(selectedProduct.id);
        resetVariantForm();
      } else {
        alert(data.message || 'Failed to save variant');
      }
    } catch (error) {
      console.error('Error saving variant:', error);
      alert('Error saving variant');
    }
  };

  const handleDeleteVariant = async (variantId, variantValue) => {
    if (!window.confirm(`Are you sure you want to delete variant "${variantValue}"?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/variants/${variantId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Variant deleted successfully');
        fetchProductVariants(selectedProduct.id);
      } else {
        alert(data.message || 'Failed to delete variant');
      }
    } catch (error) {
      console.error('Error deleting variant:', error);
      alert('Error deleting variant');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete product "${productName}"? This will also delete all variants and images.`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        alert('Product deleted successfully');
        fetchProducts();
      } else {
        alert(data.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      category_id: product.category_id || '',
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      short_description: product.short_description || '',
      price: product.price,
      sale_price: product.sale_price || '',
      sku: product.sku || '',
      stock_quantity: product.stock_quantity || '',
      brand: product.brand || '',
      frame_type: product.frame_type || 'full-rim',
      frame_shape: product.frame_shape || '',
      frame_material: product.frame_material || '',
      lens_type: product.lens_type || '',
      prescription_type: product.prescription_type || 'non-prescription',
      gender: product.gender || 'unisex',
      is_featured: product.is_featured || 0,
      is_active: product.is_active !== undefined ? product.is_active : 1
    });
    setShowAddForm(true);
  };

  const handleViewVariants = async (product) => {
    setSelectedProduct(product);
    await fetchProductVariants(product.id);
    setShowVariantsModal(true);
  };

  const handleViewImages = async (product) => {
    setSelectedProduct(product);
    setEditingProduct(product);
    await fetchProductImages(product.id);
    setShowImagesModal(true);
  };

  const handleManageVariantImages = (variant) => {
    setCurrentVariant(variant);
    fetchVariantImages(variant.id);
  };

  const resetForm = () => {
    setFormData({
      category_id: '', name: '', slug: '', description: '', short_description: '',
      price: '', sale_price: '', sku: '', stock_quantity: '', brand: '',
      frame_type: 'full-rim', frame_shape: '', frame_material: '', lens_type: '',
      prescription_type: 'non-prescription', gender: 'unisex', is_featured: 0, is_active: 1
    });
    setEditingProduct(null);
    setShowAddForm(false);
  };

  const resetVariantForm = () => {
    setVariantData({
      product_id: selectedProduct?.id || '', variant_name: 'Color',
      variant_value: '', variant_color_code: '', price: '', sale_price: '', stock_quantity: ''
    });
    setEditingVariant(null);
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setVariantData({
      product_id: selectedProduct.id, variant_name: variant.variant_name,
      variant_value: variant.variant_value, variant_color_code: variant.variant_color_code || '',
      price: variant.price, sale_price: variant.sale_price || '', stock_quantity: variant.stock_quantity || ''
    });
  };

  const formatPrice = (price) => {
    if (!price) return '₹0.00';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const getImageUrl = (product) => {
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0].image_url || null;
    }
    if (product.image_url) return product.image_url;
    return null;
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="admin-products-loading">
        <div className="spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-products-error">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const btnStyle = {
    width:'32px', height:'32px', border:'1px solid #ddd', borderRadius:'6px',
    background:'white', cursor:'pointer', display:'inline-flex', alignItems:'center',
    justifyContent:'center', fontSize:'14px', padding:0
  };

  return (
    <div className="admin-products">
      <div className="admin-products-header">
        <h1>Products Management</h1>
        <div className="header-actions">
          <div className="search-box">
            <input type="text" placeholder="Search products..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>}
          </div>
          <button className="add-btn" onClick={() => setShowAddForm(true)}>+ Add New Product</button>
          <button className="refresh-btn" onClick={fetchProducts}>⟳ Refresh</button>
        </div>
      </div>

      {/* Add/Edit Product Modal */}
      {showAddForm && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && resetForm()}>
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select value={formData.category_id} onChange={(e) => setFormData({...formData, category_id: e.target.value})}>
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Product Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Slug</label><input type="text" value={formData.slug} onChange={(e) => setFormData({...formData, slug: e.target.value})} placeholder="auto-generated" /></div>
                <div className="form-group"><label>SKU</label><input type="text" value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Price *</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required step="0.01" /></div>
                <div className="form-group"><label>Sale Price</label><input type="number" value={formData.sale_price} onChange={(e) => setFormData({...formData, sale_price: e.target.value})} step="0.01" /></div>
                <div className="form-group"><label>Stock</label><input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Brand</label><input type="text" value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} /></div>
                <div className="form-group">
                  <label>Frame Type</label>
                  <select value={formData.frame_type} onChange={(e) => setFormData({...formData, frame_type: e.target.value})}>
                    <option value="full-rim">Full Rim</option><option value="half-rim">Half Rim</option><option value="rimless">Rimless</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})}>
                    <option value="unisex">Unisex</option><option value="men">Men</option><option value="women">Women</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Frame Shape</label><input type="text" value={formData.frame_shape} onChange={(e) => setFormData({...formData, frame_shape: e.target.value})} /></div>
                <div className="form-group"><label>Frame Material</label><input type="text" value={formData.frame_material} onChange={(e) => setFormData({...formData, frame_material: e.target.value})} /></div>
                <div className="form-group"><label>Lens Type</label><input type="text" value={formData.lens_type} onChange={(e) => setFormData({...formData, lens_type: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Prescription Type</label>
                  <select value={formData.prescription_type} onChange={(e) => setFormData({...formData, prescription_type: e.target.value})}>
                    <option value="non-prescription">Non-Prescription</option><option value="single-vision">Single Vision</option><option value="bifocal">Bifocal</option><option value="progressive">Progressive</option>
                  </select>
                </div>
                <div className="form-group checkbox-group">
                  <label><input type="checkbox" checked={formData.is_featured === 1} onChange={(e) => setFormData({...formData, is_featured: e.target.checked ? 1 : 0})} /> Featured</label>
                  <label><input type="checkbox" checked={formData.is_active === 1} onChange={(e) => setFormData({...formData, is_active: e.target.checked ? 1 : 0})} /> Active</label>
                </div>
              </div>
              <div className="form-group"><label>Short Description</label><textarea value={formData.short_description} onChange={(e) => setFormData({...formData, short_description: e.target.value})} rows="2" /></div>
              <div className="form-group"><label>Full Description</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="4" /></div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">{editingProduct ? 'Update Product' : 'Create Product'}</button>
                <button type="button" className="cancel-btn" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Variants Modal */}
      {showVariantsModal && selectedProduct && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && (() => { setShowVariantsModal(false); setSelectedProduct(null); })()}>
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>Manage Variants - {selectedProduct.name}</h2>
              <button className="close-btn" onClick={() => { setShowVariantsModal(false); setSelectedProduct(null); }}>×</button>
            </div>
            <div className="variants-section">
              <h3>Add New Variant</h3>
              <form onSubmit={handleVariantSubmit} className="variant-form">
                <div className="form-row">
                  <div className="form-group"><label>Variant Name</label><input type="text" value={variantData.variant_name} disabled /></div>
                  <div className="form-group"><label>Variant Value *</label><input type="text" value={variantData.variant_value} onChange={(e) => setVariantData({...variantData, variant_value: e.target.value})} required placeholder="e.g., Black, Blue" /></div>
                  <div className="form-group"><label>Color Code</label><input type="color" value={variantData.variant_color_code} onChange={(e) => setVariantData({...variantData, variant_color_code: e.target.value})} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Price</label><input type="number" value={variantData.price} onChange={(e) => setVariantData({...variantData, price: e.target.value})} step="0.01" placeholder="Use product price" /></div>
                  <div className="form-group"><label>Sale Price</label><input type="number" value={variantData.sale_price} onChange={(e) => setVariantData({...variantData, sale_price: e.target.value})} step="0.01" /></div>
                  <div className="form-group"><label>Stock</label><input type="number" value={variantData.stock_quantity} onChange={(e) => setVariantData({...variantData, stock_quantity: e.target.value})} /></div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-btn">{editingVariant ? 'Update Variant' : 'Add Variant'}</button>
                  {editingVariant && <button type="button" className="cancel-btn" onClick={resetVariantForm}>Cancel Edit</button>}
                </div>
              </form>
            </div>
            <div className="variants-list" style={{padding:'20px'}}>
              <h3>Existing Variants</h3>
              {variants.length === 0 ? <p style={{color:'#999'}}>No variants added yet.</p> : (
                <table className="variants-table">
                  <thead><tr><th>Variant</th><th>Color</th><th>Price</th><th>Sale</th><th>Stock</th><th>Actions</th></tr></thead>
                  <tbody>
                    {variants.map(v => (
                      <tr key={v.id}>
                        <td>{v.variant_value}</td>
                        <td>{v.variant_color_code && <div className="color-preview" style={{backgroundColor:v.variant_color_code}}></div>}</td>
                        <td>{v.price ? formatPrice(v.price) : 'Default'}</td>
                        <td>{v.sale_price ? formatPrice(v.sale_price) : '-'}</td>
                        <td>{v.stock_quantity || 'Unlimited'}</td>
                        <td className="actions-cell">
                          <button style={btnStyle} onClick={() => handleEditVariant(v)}>✏️</button>
                          <button style={btnStyle} onClick={() => handleManageVariantImages(v)}>🖼️</button>
                          <button style={btnStyle} onClick={() => handleDeleteVariant(v.id, v.variant_value)}>🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Images Modal */}
      {showImagesModal && selectedProduct && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && (() => { setShowImagesModal(false); setSelectedProduct(null); })()}>
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>Manage Images - {selectedProduct.name}</h2>
              <button className="close-btn" onClick={() => { setShowImagesModal(false); setSelectedProduct(null); }}>×</button>
            </div>
            <div className="images-section" style={{padding:'20px'}}>
              <div className="upload-area" style={{padding:'20px',background:'#f8f9fa',borderRadius:'8px',textAlign:'center',marginBottom:'20px'}}>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/avif" multiple onChange={(e) => handleImageUpload(e, 'product')} style={{display:'none'}} />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImages} style={{padding:'10px 20px',background:'#3498db',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'14px'}}>
                  {uploadingImages ? 'Uploading...' : '📁 Upload Images'}
                </button>
                <p style={{fontSize:'12px',color:'#999',marginTop:'8px'}}>JPG, PNG, WEBP, AVIF • Max 5MB each</p>
              </div>
              <div className="images-gallery">
                {productImages.length === 0 ? <p style={{color:'#999',textAlign:'center'}}>No images uploaded yet.</p> : (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:'15px'}}>
                    {productImages.map(img => (
                      <div key={img.id} style={{border:'1px solid #eee',borderRadius:'8px',overflow:'hidden',background:'white'}}>
                        <img src={img.image_url} alt="" style={{width:'100%',height:'150px',objectFit:'cover'}} />
                        {img.is_primary === 1 && <div style={{background:'#27ae60',color:'white',padding:'3px 8px',fontSize:'11px',fontWeight:'600'}}>Primary</div>}
                        <div style={{padding:'8px',display:'flex',gap:'5px'}}>
                          {img.is_primary !== 1 && <button onClick={() => setPrimaryImage(img.id, 'product')} style={{flex:1,padding:'5px',background:'#3498db',color:'white',border:'none',borderRadius:'4px',fontSize:'11px',cursor:'pointer'}}>Set Primary</button>}
                          <button onClick={() => deleteImage(img.id, 'product')} style={{flex:1,padding:'5px',background:'#e74c3c',color:'white',border:'none',borderRadius:'4px',fontSize:'11px',cursor:'pointer'}}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Variant Images Modal */}
      {currentVariant && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && (() => { setCurrentVariant(null); setVariantImages([]); })()}>
          <div className="modal-content modal-medium">
            <div className="modal-header">
              <h2>Manage Images - {currentVariant.variant_value}</h2>
              <button className="close-btn" onClick={() => { setCurrentVariant(null); setVariantImages([]); }}>×</button>
            </div>
            <div className="images-section" style={{padding:'20px'}}>
              <div className="upload-area" style={{padding:'20px',background:'#f8f9fa',borderRadius:'8px',textAlign:'center',marginBottom:'20px'}}>
                <input ref={variantFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/avif" multiple onChange={(e) => handleImageUpload(e, 'variant', currentVariant.id)} style={{display:'none'}} />
                <button type="button" onClick={() => variantFileInputRef.current?.click()} disabled={uploadingImages} style={{padding:'10px 20px',background:'#9b59b6',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'14px'}}>
                  {uploadingImages ? 'Uploading...' : '📁 Upload Variant Images'}
                </button>
                <p style={{fontSize:'12px',color:'#999',marginTop:'8px'}}>Upload images for this color variant</p>
              </div>
              <div className="images-gallery">
                {variantImages.length === 0 ? <p style={{color:'#999',textAlign:'center'}}>No variant images uploaded yet.</p> : (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))',gap:'15px'}}>
                    {variantImages.map(img => (
                      <div key={img.id} style={{border:'1px solid #eee',borderRadius:'8px',overflow:'hidden',background:'white'}}>
                        <img src={img.image_url} alt="" style={{width:'100%',height:'150px',objectFit:'cover'}} />
                        {img.is_primary === 1 && <div style={{background:'#27ae60',color:'white',padding:'3px 8px',fontSize:'11px',fontWeight:'600'}}>Primary</div>}
                        <div style={{padding:'8px',display:'flex',gap:'5px'}}>
                          {img.is_primary !== 1 && <button onClick={() => setPrimaryImage(img.id, 'variant')} style={{flex:1,padding:'5px',background:'#3498db',color:'white',border:'none',borderRadius:'4px',fontSize:'11px',cursor:'pointer'}}>Set Primary</button>}
                          <button onClick={() => deleteImage(img.id, 'variant')} style={{flex:1,padding:'5px',background:'#e74c3c',color:'white',border:'none',borderRadius:'4px',fontSize:'11px',cursor:'pointer'}}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="products-table-container" style={{background:'white',borderRadius:'12px',overflow:'auto',boxShadow:'0 2px 10px rgba(0,0,0,0.05)'}}>
        <table className="products-table" style={{width:'100%',borderCollapse:'collapse',minWidth:'900px'}}>
          <thead>
            <tr style={{background:'#f8f9fa',borderBottom:'2px solid #e9ecef'}}>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>ID</th>
              <th style={{padding:'12px 15px',textAlign:'center',fontWeight:600,fontSize:'13px',width:'90px'}}>Image</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Name</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Brand</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Price</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Sale</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Stock</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Status</th>
              <th style={{padding:'12px 15px',textAlign:'left',fontWeight:600,fontSize:'13px'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => {
              const imgUrl = getImageUrl(product);
              return (
                <tr key={product.id} style={{borderBottom:'1px solid #f0f0f0'}} onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                  <td style={{padding:'12px 15px',fontSize:'13px'}}>{product.id}</td>
                  <td style={{padding:'8px',textAlign:'center'}}>
                    {imgUrl ? (
                      <img src={imgUrl} alt="" style={{width:'60px',height:'60px',objectFit:'cover',borderRadius:'6px',border:'1px solid #ddd',display:'inline-block'}} />
                    ) : (
                      <div style={{width:'60px',height:'60px',background:'#f5f5f5',borderRadius:'6px',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'11px',color:'#999',border:'1px dashed #ddd'}}>No img</div>
                    )}
                  </td>
                  <td style={{padding:'12px 15px'}}>
                    <div style={{fontWeight:'600',fontSize:'14px'}}>{product.name}</div>
                    <div style={{fontSize:'11px',color:'#999'}}>{product.sku || '-'}</div>
                  </td>
                  <td style={{padding:'12px 15px',fontSize:'13px'}}>{product.brand || '-'}</td>
                  <td style={{padding:'12px 15px',color:'#27ae60',fontWeight:'600',fontSize:'13px'}}>{formatPrice(product.price)}</td>
                  <td style={{padding:'12px 15px',color:'#e67e22',fontSize:'13px'}}>{product.sale_price ? formatPrice(product.sale_price) : '-'}</td>
                  <td style={{padding:'12px 15px',fontSize:'13px'}}>{product.stock_quantity || 0}</td>
                  <td style={{padding:'12px 15px'}}>
                    <span style={{padding:'3px 10px',borderRadius:'20px',fontSize:'12px',fontWeight:'500',background:product.is_active===1?'#e8f5e9':'#f5f5f5',color:product.is_active===1?'#27ae60':'#999'}}>
                      {product.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{padding:'12px 15px'}}>
                    <div style={{display:'flex',gap:'4px'}}>
                      <button onClick={() => handleEditProduct(product)} style={btnStyle} title="Edit">✏️</button>
                      <button onClick={() => handleViewVariants(product)} style={btnStyle} title="Variants">🎨</button>
                      <button onClick={() => handleViewImages(product)} style={btnStyle} title="Images">🖼️</button>
                      <button onClick={() => handleDeleteProduct(product.id, product.name)} style={btnStyle} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredProducts.length === 0 && <div style={{textAlign:'center',padding:'40px',color:'#999'}}>No products found.</div>}
      </div>
    </div>
  );
};

export default AdminProducts;